/* eslint-disable no-unused-vars */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { FormInput, Header, SiderBar } from 'Components'
import { useRoot, useDashboard, useRoles } from 'Hooks'
import { Button, Col, Row } from 'antd'
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { getMenu } from 'Components/siderBar'
import ModalComponent from 'Components/modal'

import { RoutesName } from 'Routes/paths'
import { getLocalStorage, removeLocalStorage, STORAGE } from 'Utils'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import PrivateTitle, { MemberManagementTableStyled } from '../Components/private-title'
import { ROLE_TYPE } from '../Constants/permission'
import { SUCCESS } from '../Stores'
import {
  CREATE_PORTFOLIO,
  UPDATE_PORTFOLIO_NAME
} from '../Modules/dashboard/store/constants'
import { createPortFolioValidation, updateNamePortFolioValidation } from './createPortFolioValidation'
import Loading from '../Components/loading'
import AbsoluteMenu from '../Components/AbsoluteMenu'

const Wrapper = styled.div`
  overflow: auto;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.bg_primary_light};
  height: 100%;
  padding: 0;
`
const Main = styled.main`
  margin-top: 63px;
  display: flex;
  width: 100%;
  padding: 0;
  background: ${({ theme }) => theme.bg_primary_light};
  justify-content: center;
  height: auto;
`

const PortFolioLink = styled(NavLink)`
  font-size: 12px;
  line-height: 16px;
  padding: 24px;
  display: inline-block;
  text-align: center;
  letter-spacing: 1px;

  max-width: 400px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  color: #374151;
  border-bottom: ${({ bottomLine }) => bottomLine || 3}px solid transparent;

  &.menu-active {
    color: #111827;
    font-weight: 700;
    border-bottom-color: ${({ theme }) => theme.primary};
  }
  
`

const Content = styled.div`
  width: 100%;

  .ant-row.content {
    align-items: center;
    padding: 10px 49px;
  }

  .portfolio-wrapper {
    background-color: #ffffff;
    border-bottom: 1px solid #e5e7eb;
  }


  .portfolio-list {
    align-items: center;
    overflow-x: auto;
    display: block;
    white-space: nowrap;

    ${PortFolioLink} {
      font-weight: 700;
    }
  }

  .private-menu {
    margin-top: 0;
    padding: 0 49px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;

    ${PortFolioLink} {
      padding: 12px 24px;
    }
  }

  .wrapper-container {
    padding: 30px 49px;
  }

  .portfolio-action {
    text-align: right;
  }

  .ant-select-selection-item {
    color: red;
  }

  @media only screen and (max-width: 1024px) {
    .private-menu {
      padding: 0 15px;
    }

    .wrapper-container,
    .ant-row.content {
      padding: 15px;
    }
  }
`

const ChangeNameTable = styled.table`
  border: 1px solid #D9D9D9;

  thead {
    tr {
      background: #3B82F6;
      border: 1px solid #D9D9D9;
    }
  }

  td {
    background: #F9F9F9;
  }

  th {
    font-style: normal;
    font-weight: 700;
    font-size: 10px;
    line-height: 20px;
    /* identical to box height, or 200% */


    color: #000000;
  }

  td {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    /* identical to box height, or 167% */
    text-align: center;
    color: #000000;
  }

  .trim-content{
    max-width: 200px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`

const ChangeNameWrapper = styled.div`
  .form-wrapper {
    margin-top: 22px;
  }

  .ant-input {
    background: #F9F9F9;
    border: 1px solid #D9D9D9 !important;
  }
`

const HomeLayout = ({ children }) => {
  const location = useLocation()
  const history = useHistory()
  const { sidebarCompact, showModalMessageAction } = useRoot()
  const { id } = useParams()
  const [visibleModal, setVisibleModal] = useState(false)
  const [visibleModalConfirm, setVisibleModalConfirm] = useState(false)
  const [isVisibleChangeNamePopup, setIsVisibleChangeNamePopup] = useState(false)
  const [positionChangeName, setPositionChangeName] = useState({
    x: null,
    y: null
  })
  const {
    isLoading,
    createPortFolioAction,
    getPortFolioByUserIdAction,
    listPortFolioByUserId,
    updateTimeIPOAction,
    getPortFolioByIdAction,
    portFolioById,
    updateTimeIPOStatus,
    listRoles,
    userInvite,
    getAssetGoalsAction,
    getGrowthSchedulesAction,
    postInviteMemberPortFolioAction,
    getAllMemberInPortfolioAction,
    listMemberInPortfolio,
    deleteMemberPortFolioAction,
    getAllPermissionRolesAction,
    success,
    isPortfolioAdmin,
    isCompanyAdmin,
    isUser,
    isEditor,
    updatePortfolioNameAction,
    getPortFolioByIdEditedAction,
    portfolioSelected
  } = useDashboard()

  const { t } = useTranslation(['menu', 'common'])

  const form = useForm({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(createPortFolioValidation(t))
  })
  const { handleSubmit, resetField } = form
  const localPath = location.pathname.split('/')[1]
  const isLearningDataRoute = localPath === 'learning-data'
  const portfolioLinkSegment = isLearningDataRoute ? 'dashboard' : localPath

  const changeNamePortfolioForm = useForm({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(updateNamePortFolioValidation(t))
  })

  const { handleSubmit: handleSubmitChangeName, reset } = changeNamePortfolioForm

  const titleAndSub = useMemo(() => {
    const path = `/${localPath}`
    switch (path) {
      case RoutesName.DASHBOARD:
        return {
          title: 'dashboard',
          subTitle: 'checkReviewAndGoals'
        }
      case RoutesName.WEEKLY:
        return {
          title: 'weekly_management',
          subTitle: 'checkReviewAndGoals'
        }
      case RoutesName.LEARNING_DATA:
        return {
          title: 'learning_data_title',
          subTitle: 'learning_data_intro'
        }
      default:
        return {
          title: '',
          subTitle: ''
        }
    }
  }, [location.pathname])

  useEffect(() => {
    reset()
  }, [isVisibleChangeNamePopup])

  function onSubmit(values) {
    createPortFolioAction(values)
  }

  function changeNameSubmit(values) {
    updatePortfolioNameAction({
      name: { ...values },
      id: portfolioSelected?.id
    })
  }

  useEffect(() => {
    getPortFolioByUserIdAction()
  }, [])

  useEffect(() => {
    if (success === SUCCESS(CREATE_PORTFOLIO)) {
      setVisibleModal(false)
      resetField('clientName')
    }
    if (success === SUCCESS(UPDATE_PORTFOLIO_NAME)) {
      getPortFolioByIdAction({ id: portfolioSelected.id })
      reset()
    }
  }, [success])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isLearningDataRoute) {
      return undefined
    }
    if (!getLocalStorage('getPortfolioId')) {
      if (!id && listPortFolioByUserId) {
        return history.push(`/${localPath}/${listPortFolioByUserId.length > 0 && listPortFolioByUserId[0].id}`)
      }
    } else {
      return history.push(`/${localPath}/${getLocalStorage('getPortfolioId')}`)
    }
    return undefined
  }, [id, listPortFolioByUserId, isLearningDataRoute, localPath, history])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isLearningDataRoute) {
      return undefined
    }
    if (listPortFolioByUserId?.length === 0) {
      return history.push(`/${localPath}`)
    }
    return undefined
  }, [listPortFolioByUserId, isLearningDataRoute, localPath, history])

  useEffect(() => {
    if (visibleModal === false) {
      resetField('clientName')
    }
  }, [visibleModal])
  const absoluteMenuRef = useRef()

  const onContextMenu = (portfolioItem, e) => {
    e.preventDefault()
    setPositionChangeName({
      x: e.clientX,
      y: e.clientY
    })
    absoluteMenuRef.current.open(portfolioItem.id)
  }

  const [updatePortfolioName, setUpdatePortfolioName] = useState(false)

  useEffect(() => {
    if (success === SUCCESS(UPDATE_PORTFOLIO_NAME)) {
      setUpdatePortfolioName(true)
    }
  }, [success])

  useEffect(() => {
    if (id) {
      getPortFolioByIdAction({ id })
    }
  }, [id])

  const renderChangeNamePortfolio = () => (
    <ChangeNameWrapper>
      <ChangeNameTable
        width="100%"
        style={{
          marginTop: 28
        }}
      >
        {isLoading && <Loading loadingMini />}
        <thead>
          <tr>
            <th>PORTFOLIO NAME</th>
            <th>STARTED FORM</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td className="trim-content" title={portfolioSelected?.client_name}>{portfolioSelected?.client_name}</td>
            <td>{moment(portfolioSelected?.created_at).format('DD MMM YYYY')}</td>
          </tr>
        </tbody>

      </ChangeNameTable>
      {
        !updatePortfolioName ? (
          <FormProvider {...changeNamePortfolioForm}>
            <div className="form-wrapper">
              <FormInput name="name" label={t('common:enter_new_simulation_name')} />
            </div>
          </FormProvider>
        ) : (
          <div
            style={{
              marginTop: 22,
              textAlign: 'center'
            }}
          >
            <b><p>{t('common:simulation_name_changed')}</p></b>
          </div>
        )
      }
    </ChangeNameWrapper>
  )
  const renderCreatePortFolioForm = () => (
    <>
      {isLoading && <Loading loadingMini />}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            label={t('common:simulation_name')}
            placeholder={t('common:simulation_name_placeholder')}
            name="clientName"
          />
        </form>
      </FormProvider>
    </>
  )
  return (
    <Wrapper sidebarCompact={sidebarCompact}>
      <Header />
      <Main>
        <SiderBar />
        <Content>
          <div className="portfolio-wrapper">
            <Row className="content">
              <Col span={18} className="portfolio-list">
                {
                  listPortFolioByUserId?.map((portfolioItem, index) => (portfolioItem.role === 'COMPANY_ADMIN' ? (
                    <PortFolioLink
                      key={index}
                      bottomLine={4}
                      activeClassName="menu-active"
                      to={`/${portfolioLinkSegment}/${portfolioItem?.id}`}
                      onClick={() => absoluteMenuRef.current.close()}
                      onContextMenu={(e) => onContextMenu(portfolioItem, e)}
                    >
                      {portfolioItem?.client_name}
                    </PortFolioLink>
                  ) : (
                    <PortFolioLink
                      key={index}
                      bottomLine={4}
                      activeClassName="menu-active"
                      to={`/${portfolioLinkSegment}/${portfolioItem?.id}`}
                    >
                      {portfolioItem?.client_name}
                    </PortFolioLink>
                  )))
                }
              </Col>
              {
                listPortFolioByUserId?.length > 0 && getLocalStorage(STORAGE.ROLE) === ROLE_TYPE.COMPANY_ADMIN && (
                  <Col span={6} className="portfolio-action">
                    <Button type="primary" onClick={() => setVisibleModal(true)}>{t('common:create_portfolio')}</Button>
                  </Col>
                )
              }
            </Row>
          </div>

          {
            isLearningDataRoute ? (
              <div className="wrapper-container" style={{ padding: '24px 49px' }}>
                {children}
              </div>
            ) : id ? (
              <>
                <Row className="private-menu">
                  {getMenu(t, id).map((item, index) => (
                    <PortFolioLink key={index} to={item.to} activeClassName="menu-active">
                      {item.label}
                    </PortFolioLink>
                  ))}
                </Row>

                <Row className="private-menu">
                  <PrivateTitle
                    title={t(`menu:${titleAndSub.title}`)}
                    subTitle={t(`menu:${titleAndSub.subTitle}`)}
                    isShowProjectInfo={localPath === 'dashboard'}
                    timeToIPO
                    portFolioById={portFolioById}
                    updateTimeIPOAction={updateTimeIPOAction}
                    getPortFolioByIdAction={getPortFolioByIdAction}
                    updateTimeIPOStatus={updateTimeIPOStatus}
                    getAssetGoalsAction={getAssetGoalsAction}
                    getGrowthSchedulesAction={getGrowthSchedulesAction}
                    postInviteMemberPortFolioAction={postInviteMemberPortFolioAction}
                    getAllMemberInPortfolioAction={getAllMemberInPortfolioAction}
                    listRoles={listRoles}
                    listMemberInPortfolio={listMemberInPortfolio}
                    deleteMemberPortFolioAction={deleteMemberPortFolioAction}
                    success={success}
                    showModalMessageAction={showModalMessageAction}
                    getAllPermissionRolesAction={getAllPermissionRolesAction}
                    isPortfolioAdmin={isPortfolioAdmin}
                    isCompanyAdmin={isCompanyAdmin}
                    isUser={isUser}
                    isEditor={isEditor}
                  />
                </Row>
                <div className="wrapper-container">
                  {children}
                </div>
              </>
            ) : (
              <div className="wrapper-container" style={{ padding: '60px 49px', maxWidth: 480, margin: '0 auto' }}>
                {isLoading ? (
                  <Loading loadingMini />
                ) : (
                  <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <FormInput
                        label={t('common:simulation_name')}
                        placeholder={t('common:simulation_name_placeholder')}
                        name="clientName"
                      />
                      <Button type="primary" size="large" htmlType="submit" style={{ marginTop: 24, width: '100%' }}>
                        {t('common:create_portfolio')}
                      </Button>
                    </form>
                  </FormProvider>
                )}
              </div>
            )
          }
        </Content>
      </Main>

      <ModalComponent
        title={t('common:create_portfolio')}
        themeMode="light"
        onCancel={() => setVisibleModal(false)}
        visible={visibleModal}
        onSubmit={handleSubmit(onSubmit)}
        modalWith={412}
      >
        {renderCreatePortFolioForm()}
      </ModalComponent>

      <ModalComponent
        title={t('common:create_portfolio')}
        themeMode="light"
        onCancel={() => setVisibleModalConfirm(false)}
        visible={visibleModalConfirm}
        onSubmit={() => setVisibleModalConfirm(false)}
        modalWith={412}
        cancel={false}
        showCloseButton={false}
      >
        <Trans
          t={t}
          components={{
            br: <br />
          }}
        >
          common:addedPortfolioMessage
        </Trans>
      </ModalComponent>

      <ModalComponent
        title={t('common:change_name_modal')}
        themeMode="light"
        cancel={!updatePortfolioName}
        showCloseText={t('common:cancel')}
        onCancel={() => {
          setIsVisibleChangeNamePopup(false)
          setUpdatePortfolioName(false)
        }}
        visible={isVisibleChangeNamePopup}
        onSubmit={!updatePortfolioName ? handleSubmitChangeName(changeNameSubmit) : () => {
          setIsVisibleChangeNamePopup(false)
          setUpdatePortfolioName(false)
        }}
        modalWith={412}
        onSubmitText={!updatePortfolioName ? t('common:rename') : t('common:ok')}
      >
        {renderChangeNamePortfolio()}
      </ModalComponent>
      <AbsoluteMenu ref={absoluteMenuRef} positionX={positionChangeName.x} positionY={positionChangeName.y}>
        <button onClick={() => {
          getPortFolioByIdEditedAction({ id: absoluteMenuRef.current.getId() })
          setIsVisibleChangeNamePopup(true)
          absoluteMenuRef.current.close()
          setUpdatePortfolioName(false)
        }}
        >
          <span>Change Name</span>
        </button>
      </AbsoluteMenu>
    </Wrapper>
  )
}

export default React.memo(HomeLayout)
